import * as stylex from '@stylexjs/stylex'
import { memo, type ComponentPropsWithoutRef, type Dispatch, type ElementType, type ForwardedRef, type ReactNode, type SetStateAction } from 'react'
import isEqual from 'react-fast-compare'
import { makeExtendStyle, type ExtendStyleProps } from '../utils/makeExtendStyle/makeExtendStyle'

export type BoxProps<RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement> = {
  /**
   * 自訂渲染的 HTML 標籤
   * @default 'div'
   */
  'component'?: RootComponent
  /**
   * 子元素
   */
  'children'?: ReactNode
  /**
   * 自訂根元素樣式（StyleX）
   */
  'rootStyle'?: stylex.StyleXStyles
  /**
   * 根元素的 ref
   */
  'rootRef'?: ForwardedRef<BoxElement> | Dispatch<SetStateAction<BoxElement | null>>
  /**
   * 測試用的 data-testid 屬性
   */
  'data-testid'?: string
} & ExtendStyleProps & ComponentPropsWithoutRef<RootComponent>

/**
 * Box 是最基礎的排版容器元件，支援 ExtendStyleProps（gap、padding、margin 等）。
 * 數值型的 ExtendStyleProps 會乘以 4px（例如 padding={4} = 16px）。
 * 可透過 component prop 渲染為任意 HTML 標籤。
 *
 * @example
 * ```tsx
 * <Box padding={4} display="flex" gap={2}>內容</Box>
 * <Box component="section" borderColor="grey" borderWidth={1}>區塊</Box>
 * ```
 */
const BoxComponent = <RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement>(props: BoxProps<RootComponent, BoxElement>) => {
  const { component = 'div', extendStyles, rootStyle, rootRef, ...rest } = makeExtendStyle(props)
  const Component = component as ElementType
  return <Component ref={rootRef} {...stylex.props(rootStyle, extendStyles)} {...rest} />
}

export const Box = memo(BoxComponent, isEqual) as typeof BoxComponent
